import type { ReactNode } from 'react'
import type { ScanBarState } from '../shell/scanFocus'
import { StationShell } from '../ui/StationShell'
import type { CameraState } from '../ui/ScanBar'

/**
 * The pick list, sorted by walk order (PIK-02). Nothing on this screen names a
 * customer, an address or a rupee value - the server strips it (SEC-09) and the
 * component has no prop that could carry it back in.
 *
 * A wrong scan renders in the reject state with the words "Back on shelf", not a
 * red row alone: colour is never the only signal.
 */
export interface PickLine {
  unitId: string
  title: string
  binLabel: string
  walkSequence: number
}

export interface PickingProps {
  scan: ScanBarState
  camera?: CameraState
  currentBin: { id: string; label: string; walkSequence: number } | null
  toPick: PickLine[]
  picked: Array<{ unitId: string; title: string }>
  pendingDrafts?: number
  online?: boolean
  /** The loading, error or empty verdict, rendered above the work rather than
   *  instead of it - a failed refresh must not take the scan bar with it. */
  notice?: ReactNode
}

export function Picking({ scan, camera, currentBin, toPick, picked, pendingDrafts = 0, online = true, notice }: PickingProps) {
  const total = toPick.length + picked.length
  const rejected = scan.lastScan?.result === 'reject' ? scan.lastScan : null
  const ordered = [...toPick].sort((a, b) => a.walkSequence - b.walkSequence)

  return (
    <StationShell
      title="Pick"
      progress={`${picked.length} of ${total} picked`}
      queue={toPick.length}
      scan={scan}
      camera={camera}
      pendingDrafts={pendingDrafts}
      online={online}
    >
      {notice}
      <section className="tile tile-bin">
        <span className="tile-glyph" data-glyph="bin" aria-hidden="true" />
        <span className="t-title">{currentBin ? currentBin.label : 'Scan a bin'}</span>
        {currentBin ? <span className="walk-order">{currentBin.walkSequence}</span> : null}
      </section>

      {rejected ? (
        <p className="row row-reject reject-shake">
          <span className="t-scan">{rejected.code}</span>
          <span>{rejected.message}</span>
        </p>
      ) : null}

      <ul className="row-list progress-drain">
        {ordered.map((line) => (
          <li key={line.unitId} className="row">
            <span className="walk-order">{line.walkSequence}</span>
            <span className="bin-label">{line.binLabel}</span>
            <span>{line.title}</span>
          </li>
        ))}
      </ul>

      <ul className="row-list row-list-done">
        {picked.map((p) => (
          <li key={p.unitId} className="row row-picked">
            <span>{p.title}</span>
          </li>
        ))}
      </ul>
    </StationShell>
  )
}
